import React, { useState, useRef } from 'react';
import { Plus, Edit, Trash2, FileText, Copy, Save, X, Upload, Brain, Download, FileUp, AlertCircle, BookOpen } from 'lucide-react';
import AIService from '../../services/aiService';
import ClauseSelector from './ClauseSelector';

const ClauseManager = ({ clauses = [], onClausesChange, contractType }) => {
  const [showAddClause, setShowAddClause] = useState(false);
  const [editingClause, setEditingClause] = useState(null);
  const [showUploadClause, setShowUploadClause] = useState(false);
  const [showAIGeneration, setShowAIGeneration] = useState(false);
  const [showClauseSelector, setShowClauseSelector] = useState(false);
  const [isGeneratingAI, setIsGeneratingAI] = useState(false);
  const [uploadedClauseFile, setUploadedClauseFile] = useState(null);
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiGeneratedClauses, setAiGeneratedClauses] = useState([]);
  const [aiError, setAiError] = useState(null);
  const [isUploadingFile, setIsUploadingFile] = useState(false);
  const fileInputRef = useRef(null);
  
  const [newClause, setNewClause] = useState({
    id: '',
    title: '',
    content: '',
    type: 'Standard',
    category: 'General',
    isRequired: false,
    isCustom: false
  });

  // Predefined clause templates
  const clauseTemplates = {
    'Service Agreement': [
      {
        id: 'sa-1',
        title: 'Service Description',
        content: 'The Service Provider shall provide the following services: [DESCRIBE SERVICES].',
        type: 'Standard',
        category: 'Service Terms',
        isRequired: true
      },
      {
        id: 'sa-2',
        title: 'Payment Terms',
        content: 'Payment shall be made within [X] days of invoice receipt. Late payments may incur interest at [X]% per annum.',
        type: 'Standard',
        category: 'Financial',
        isRequired: true
      },
      {
        id: 'sa-3',
        title: 'Termination Clause',
        content: 'Either party may terminate this agreement with [X] days written notice.',
        type: 'Standard',
        category: 'Termination',
        isRequired: false
      }
    ],
    'Purchase Order': [
      {
        id: 'po-1',
        title: 'Delivery Terms',
        content: 'Goods shall be delivered to [ADDRESS] by [DATE]. Risk of loss transfers upon delivery.',
        type: 'Standard',
        category: 'Delivery',
        isRequired: true
      },
      {
        id: 'po-2',
        title: 'Quality Standards',
        content: 'All goods must meet the specifications outlined in this purchase order.',
        type: 'Standard',
        category: 'Quality',
        isRequired: true
      }
    ],
    'Lease Agreement': [
      {
        id: 'la-1',
        title: 'Lease Term',
        content: 'The lease term shall be [X] years commencing on [START DATE] and ending on [END DATE].',
        type: 'Standard',
        category: 'Term',
        isRequired: true
      },
      {
        id: 'la-2',
        title: 'Rent Payment',
        content: 'Monthly rent of $[AMOUNT] is due on the [DAY] of each month.',
        type: 'Standard',
        category: 'Financial',
        isRequired: true
      }
    ]
  };

  const handleAddClause = () => {
    if (newClause.title && newClause.content) {
      const clause = {
        ...newClause,
        id: Date.now().toString(),
        isCustom: true
      };
      onClausesChange([...clauses, clause]);
      setNewClause({
        id: '',
        title: '',
        content: '',
        type: 'Standard',
        category: 'General',
        isRequired: false,
        isCustom: false
      });
      setShowAddClause(false);
    }
  };

  const handleEditClause = (clause) => {
    setEditingClause(clause);
    setNewClause(clause);
    setShowAddClause(true);
  };

  const handleUpdateClause = () => {
    if (editingClause && newClause.title && newClause.content) {
      const updatedClauses = clauses.map(clause =>
        clause.id === editingClause.id ? { ...newClause, id: editingClause.id } : clause
      );
      onClausesChange(updatedClauses);
      setEditingClause(null);
      setNewClause({
        id: '',
        title: '',
        content: '',
        type: 'Standard',
        category: 'General',
        isRequired: false,
        isCustom: false
      });
      setShowAddClause(false);
    }
  };

  const handleDeleteClause = (clauseId) => {
    onClausesChange(clauses.filter(clause => clause.id !== clauseId));
  };

  const handleAddTemplateClause = (templateClause) => {
    const clause = {
      ...templateClause,
      id: Date.now().toString(),
      isCustom: false
    };
    onClausesChange([...clauses, clause]);
  };

  const handleCancel = () => {
    setShowAddClause(false);
    setShowUploadClause(false);
    setShowAIGeneration(false);
    setEditingClause(null);
    setUploadedClauseFile(null);
    setAiPrompt('');
    setAiGeneratedClauses([]);
    setAiError(null);
    setIsGeneratingAI(false);
    setIsUploadingFile(false);
    setNewClause({
      id: '',
      title: '',
      content: '',
      type: 'Standard',
      category: 'General',
      isRequired: false,
      isCustom: false
    });
  };

  // File upload handlers
  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      setUploadedClauseFile(file);
      setIsUploadingFile(true);
      setAiError(null);
      
      try {
        const response = await AIService.extractClausesFromDocument(file);
        
        if (response.success) {
          setAiGeneratedClauses(response.clauses);
        } else {
          throw new Error(response.error || 'Failed to extract clauses from document');
        }
      } catch (error) {
        console.error('Error extracting clauses from file:', error);
        setAiError(error.message);
        
        // Fallback to simulated extraction
        const fallbackClauses = extractClausesFromFile(file);
        setAiGeneratedClauses(fallbackClauses);
      } finally {
        setIsUploadingFile(false);
      }
    }
  };

  const extractClausesFromFile = (file) => {
    // Simulate clause extraction from uploaded file
    return [
      {
        id: Date.now().toString() + '_1',
        title: 'Payment Terms from Document',
        content: 'Payment shall be made within 30 days of invoice receipt. Late payments may incur interest at 1.5% per month.',
        type: 'Standard',
        category: 'Financial',
        isRequired: true,
        isCustom: false,
        source: 'uploaded_file'
      },
      {
        id: Date.now().toString() + '_2',
        title: 'Termination Clause from Document',
        content: 'Either party may terminate this agreement with 60 days written notice.',
        type: 'Standard',
        category: 'Termination',
        isRequired: false,
        isCustom: false,
        source: 'uploaded_file'
      }
    ];
  };

  // AI generation handlers
  const handleAIGeneration = async () => {
    if (!aiPrompt.trim()) return;
    
    setIsGeneratingAI(true);
    setAiError(null);
    
    try {
      const context = {
        prompt: aiPrompt,
        contractType: contractType,
        clauseCount: 3
      };
      
      const response = await AIService.generateClause(context);
      
      if (response.success && response.data && response.data.clauses) {
        setAiGeneratedClauses(response.data.clauses);
      } else {
        throw new Error(response.error || 'Failed to generate clauses');
      }
    } catch (error) {
      console.error('Error generating clauses:', error);
      setAiError(error.message);
      
      // Fallback to simulated generation
      const fallbackClauses = generateAIClauses(aiPrompt, contractType);
      setAiGeneratedClauses(fallbackClauses);
    } finally {
      setIsGeneratingAI(false);
    }
  };

  const generateAIClauses = (prompt, contractType) => {
    // Simulate AI-generated clauses based on prompt and contract type
    const baseClauses = [
      {
        id: Date.now().toString() + '_ai_1',
        title: 'AI Generated: Service Scope',
        content: `Based on your requirements: ${prompt}. The service provider shall deliver comprehensive services including but not limited to the specified requirements.`,
        type: 'AI Generated',
        category: 'Service Terms',
        isRequired: true,
        isCustom: false,
        source: 'ai_generated'
      },
      {
        id: Date.now().toString() + '_ai_2',
        title: 'AI Generated: Performance Standards',
        content: `Performance metrics shall be established based on industry standards and the specific requirements: ${prompt}. Regular performance reviews will be conducted.`,
        type: 'AI Generated',
        category: 'Quality',
        isRequired: false,
        isCustom: false,
        source: 'ai_generated'
      },
      {
        id: Date.now().toString() + '_ai_3',
        title: 'AI Generated: Risk Management',
        content: `Risk mitigation strategies shall be implemented to address potential issues related to: ${prompt}. Regular risk assessments will be performed.`,
        type: 'AI Generated',
        category: 'General',
        isRequired: false,
        isCustom: false,
        source: 'ai_generated'
      }
    ];
    
    return baseClauses;
  };

  const handleAddGeneratedClause = (clause) => {
    const newClause = {
      ...clause,
      id: Date.now().toString(),
      isCustom: true
    };
    onClausesChange([...clauses, newClause]);
  };

  const handleAddAllGeneratedClauses = () => {
    if (aiGeneratedClauses && aiGeneratedClauses.length > 0) {
      const newClauses = aiGeneratedClauses.map(clause => ({
        ...clause,
        id: Date.now().toString() + '_' + Math.random(),
        isCustom: true
      }));
      onClausesChange([...clauses, ...newClauses]);
      setAiGeneratedClauses([]);
    }
  };

  const getCategoryColor = (category) => {
    const colors = {
      'Service Terms': '#3b82f6',
      'Financial': '#10b981',
      'Termination': '#f59e0b',
      'Delivery': '#8b5cf6',
      'Quality': '#ef4444',
      'Term': '#06b6d4',
      'General': '#6b7280'
    };
    return colors[category] || '#6b7280';
  };

  return (
    <div style={{ width: '100%' }}>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: '16px' 
      }}>
        <h6 style={{ 
          margin: 0, 
          color: '#374151', 
          fontWeight: '600',
          fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
        }}>
          Contract Clauses ({clauses.length})
        </h6>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            type="button"
            onClick={() => setShowClauseSelector(true)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '8px 12px',
              backgroundColor: '#f59e0b',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: '12px',
              fontWeight: '500',
              cursor: 'pointer',
              fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
            }}
          >
            <BookOpen size={14} />
            Select from Library
          </button>
          <button
            type="button"
            onClick={() => setShowAddClause(true)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '8px 12px',
              backgroundColor: '#007B8F',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: '12px',
              fontWeight: '500',
              cursor: 'pointer',
              fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
            }}
          >
            <Plus size={14} />
            Manual
          </button>
          <button
            type="button"
            onClick={() => setShowUploadClause(true)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '8px 12px',
              backgroundColor: '#10b981',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: '12px',
              fontWeight: '500',
              cursor: 'pointer',
              fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
            }}
          >
            <Upload size={14} />
            Upload
          </button>
          <button
            type="button"
            onClick={() => setShowAIGeneration(true)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '8px 12px',
              backgroundColor: '#8b5cf6',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: '12px',
              fontWeight: '500',
              cursor: 'pointer',
              fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
            }}
          >
            <Brain size={14} />
            AI Generate
          </button>
        </div>
      </div>

      {/* Template Clauses */}
      {contractType && clauseTemplates[contractType] && (
        <div style={{ marginBottom: '16px' }}>
          <div style={{ 
            fontSize: '12px', 
            fontWeight: '500', 
            color: '#6b7280', 
            marginBottom: '8px' 
          }}>
            Template Clauses for {contractType}
          </div>
          <div style={{ 
            display: 'flex', 
            gap: '8px', 
            flexWrap: 'wrap' 
          }}>
            {clauseTemplates[contractType].map((template) => (
              <button
                key={template.id}
                type="button"
                onClick={() => handleAddTemplateClause(template)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  padding: '6px 10px',
                  backgroundColor: '#f3f4f6',
                  color: '#374151',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  fontSize: '11px',
                  cursor: 'pointer',
                  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
                }}
              >
                <Copy size={12} />
                {template.title}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* File Upload Form */}
      {showUploadClause && (
        <div style={{
          border: '1px solid #e5e7eb',
          borderRadius: '8px',
          padding: '16px',
          backgroundColor: '#f0fdf4',
          marginBottom: '16px'
        }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            marginBottom: '12px' 
          }}>
            <h6 style={{ 
              margin: 0, 
              color: '#374151', 
              fontWeight: '600',
              fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
            }}>
              <Upload size={16} style={{ marginRight: '8px', color: '#10b981' }} />
              Upload Contract Document
            </h6>
            <button
              type="button"
              onClick={handleCancel}
              style={{
                background: 'none',
                border: 'none',
                color: '#6b7280',
                cursor: 'pointer',
                padding: '4px'
              }}
            >
              <X size={16} />
            </button>
          </div>

          <div style={{ marginBottom: '12px' }}>
            <div style={{ 
              border: '2px dashed #10b981', 
              borderRadius: '8px', 
              padding: '20px', 
              textAlign: 'center',
              backgroundColor: 'white',
              cursor: 'pointer'
            }}
            onClick={() => fileInputRef.current?.click()}
            >
              <FileUp size={32} style={{ color: '#10b981', marginBottom: '8px' }} />
              <div style={{ fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>
                Click to upload contract document
              </div>
              <div style={{ fontSize: '12px', color: '#6b7280' }}>
                PDF, DOC, DOCX files supported
              </div>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.doc,.docx"
              onChange={handleFileUpload}
              style={{ display: 'none' }}
            />
          </div>

          {uploadedClauseFile && (
            <div style={{ 
              padding: '8px 12px', 
              backgroundColor: isUploadingFile ? '#fef3c7' : '#dcfce7', 
              borderRadius: '6px', 
              fontSize: '12px',
              color: isUploadingFile ? '#92400e' : '#166534',
              fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
            }}>
              {isUploadingFile ? (
                <>
                  <div style={{ 
                    width: '12px', 
                    height: '12px', 
                    border: '2px solid #92400e40', 
                    borderTop: '2px solid #92400e', 
                    borderRadius: '50%', 
                    display: 'inline-block',
                    marginRight: '8px',
                    animation: 'spin 1s linear infinite' 
                  }} />
                  Processing file: {uploadedClauseFile.name}
                </>
              ) : (
                <>✓ File uploaded: {uploadedClauseFile.name}</>
              )}
            </div>
          )}
        </div>
      )}

      {/* AI Generation Form */}
      {showAIGeneration && (
        <div style={{
          border: '1px solid #e5e7eb',
          borderRadius: '8px',
          padding: '16px',
          backgroundColor: '#faf5ff',
          marginBottom: '16px'
        }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            marginBottom: '12px' 
          }}>
            <h6 style={{ 
              margin: 0, 
              color: '#374151', 
              fontWeight: '600',
              fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
            }}>
              <Brain size={16} style={{ marginRight: '8px', color: '#8b5cf6' }} />
              AI-Powered Clause Generation
            </h6>
            <button
              type="button"
              onClick={handleCancel}
              style={{
                background: 'none',
                border: 'none',
                color: '#6b7280',
                cursor: 'pointer',
                padding: '4px'
              }}
            >
              <X size={16} />
            </button>
          </div>

          <div style={{ marginBottom: '12px' }}>
            <label style={{ 
              display: 'block', 
              fontSize: '12px', 
              fontWeight: '500', 
              color: '#374151', 
              marginBottom: '6px' 
            }}>
              Describe your contract requirements
            </label>
            <textarea
              value={aiPrompt}
              onChange={(e) => setAiPrompt(e.target.value)}
              rows="3"
              style={{
                width: '100%',
                padding: '8px 10px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '12px',
                resize: 'vertical',
                fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
              }}
              placeholder="e.g., 'We need clauses for a software development contract with milestone-based payments, intellectual property protection, and data security requirements'"
            />
          </div>

          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center' 
          }}>
            <div style={{ fontSize: '11px', color: '#6b7280' }}>
              AI will generate relevant clauses based on your description
            </div>
            <button
              type="button"
              onClick={handleAIGeneration}
              disabled={!aiPrompt.trim() || isGeneratingAI}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '8px 16px',
                backgroundColor: isGeneratingAI ? '#9ca3af' : '#8b5cf6',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '12px',
                fontWeight: '500',
                cursor: isGeneratingAI ? 'not-allowed' : 'pointer',
                fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
              }}
            >
              {isGeneratingAI ? (
                <>
                  <div style={{ 
                    width: '12px', 
                    height: '12px', 
                    border: '2px solid #ffffff40', 
                    borderTop: '2px solid #ffffff', 
                    borderRadius: '50%', 
                    animation: 'spin 1s linear infinite' 
                  }} />
                  Generating...
                </>
              ) : (
                <>
                  <Brain size={12} />
                  Generate Clauses
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Error Display */}
      {aiError && (
        <div style={{
          border: '1px solid #fecaca',
          borderRadius: '8px',
          padding: '12px',
          backgroundColor: '#fef2f2',
          marginBottom: '16px'
        }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px',
            color: '#dc2626',
            fontSize: '12px',
            fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
          }}>
            <AlertCircle size={16} />
            <span style={{ fontWeight: '500' }}>AI Service Error:</span>
            <span>{aiError}</span>
          </div>
          <div style={{ 
            fontSize: '11px', 
            color: '#7f1d1d', 
            marginTop: '4px',
            fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
          }}>
            Using fallback generation. Please check your connection and try again.
          </div>
        </div>
      )}

      {/* Generated Clauses Preview */}
      {aiGeneratedClauses && aiGeneratedClauses.length > 0 && (
        <div style={{
          border: '1px solid #e5e7eb',
          borderRadius: '8px',
          padding: '16px',
          backgroundColor: '#fefce8',
          marginBottom: '16px'
        }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            marginBottom: '12px' 
          }}>
            <h6 style={{ 
              margin: 0, 
              fontSize: '13px', 
              fontWeight: '600', 
              color: '#374151',
              fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
            }}>
              Generated Clauses ({aiGeneratedClauses ? aiGeneratedClauses.length : 0})
            </h6>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                type="button"
                onClick={handleAddAllGeneratedClauses}
                style={{
                  padding: '4px 8px',
                  backgroundColor: '#10b981',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  fontSize: '11px',
                  cursor: 'pointer',
                  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
                }}
              >
                Add All
              </button>
              <button
                type="button"
                onClick={() => setAiGeneratedClauses([])}
                style={{
                  padding: '4px 8px',
                  backgroundColor: '#ef4444',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  fontSize: '11px',
                  cursor: 'pointer',
                  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
                }}
              >
                Clear
              </button>
            </div>
          </div>

          <div style={{ 
            border: '1px solid #e5e7eb', 
            borderRadius: '6px', 
            backgroundColor: 'white',
            maxHeight: '300px',
            overflowY: 'auto'
          }}>
            {aiGeneratedClauses && aiGeneratedClauses.map((clause, index) => (
              <div
                key={clause.id}
                style={{
                  padding: '12px',
                  borderBottom: index < (aiGeneratedClauses ? aiGeneratedClauses.length - 1 : 0) ? '1px solid #f3f4f6' : 'none',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  gap: '12px'
                }}
              >
                <div style={{ flex: 1 }}>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '8px', 
                    marginBottom: '4px' 
                  }}>
                    <h6 style={{ 
                      margin: 0, 
                      fontSize: '12px', 
                      fontWeight: '600', 
                      color: '#374151',
                      fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
                    }}>
                      {clause.title}
                    </h6>
                    <span style={{
                      padding: '2px 6px',
                      backgroundColor: clause.source === 'ai_generated' ? '#8b5cf6' : '#10b981',
                      color: 'white',
                      borderRadius: '4px',
                      fontSize: '10px',
                      fontWeight: '500'
                    }}>
                      {clause.source === 'ai_generated' ? 'AI' : 'Upload'}
                    </span>
                    <span style={{
                      padding: '2px 6px',
                      backgroundColor: getCategoryColor(clause.category),
                      color: 'white',
                      borderRadius: '4px',
                      fontSize: '10px',
                      fontWeight: '500'
                    }}>
                      {clause.category}
                    </span>
                  </div>
                  <div style={{ 
                    fontSize: '11px', 
                    color: '#6b7280', 
                    lineHeight: '1.4',
                    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
                  }}>
                    {clause.content}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => handleAddGeneratedClause(clause)}
                  style={{
                    padding: '4px 8px',
                    backgroundColor: '#007B8F',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    fontSize: '10px',
                    cursor: 'pointer',
                    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
                  }}
                >
                  Add
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Add/Edit Clause Form */}
      {showAddClause && (
        <div style={{
          border: '1px solid #e5e7eb',
          borderRadius: '8px',
          padding: '16px',
          backgroundColor: '#f9fafb',
          marginBottom: '16px'
        }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            marginBottom: '12px' 
          }}>
            <h6 style={{ 
              margin: 0, 
              color: '#374151', 
              fontWeight: '600',
              fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
            }}>
              {editingClause ? 'Edit Clause' : 'Add New Clause'}
            </h6>
            <button
              type="button"
              onClick={handleCancel}
              style={{
                background: 'none',
                border: 'none',
                color: '#6b7280',
                cursor: 'pointer',
                padding: '4px'
              }}
            >
              <X size={16} />
            </button>
          </div>

          <div style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
            <div style={{ flex: 1 }}>
              <label style={{ 
                display: 'block', 
                fontSize: '12px', 
                fontWeight: '500', 
                color: '#374151', 
                marginBottom: '4px' 
              }}>
                Clause Title
              </label>
              <input
                type="text"
                value={newClause.title}
                onChange={(e) => setNewClause({ ...newClause, title: e.target.value })}
                style={{
                  width: '100%',
                  padding: '8px 10px',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  fontSize: '12px',
                  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
                }}
                placeholder="Enter clause title"
              />
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ 
                display: 'block', 
                fontSize: '12px', 
                fontWeight: '500', 
                color: '#374151', 
                marginBottom: '4px' 
              }}>
                Category
              </label>
              <select
                value={newClause.category}
                onChange={(e) => setNewClause({ ...newClause, category: e.target.value })}
                style={{
                  width: '100%',
                  padding: '8px 10px',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  fontSize: '12px',
                  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
                }}
              >
                <option value="General">General</option>
                <option value="Service Terms">Service Terms</option>
                <option value="Financial">Financial</option>
                <option value="Termination">Termination</option>
                <option value="Delivery">Delivery</option>
                <option value="Quality">Quality</option>
                <option value="Term">Term</option>
              </select>
            </div>
          </div>

          <div style={{ marginBottom: '12px' }}>
            <label style={{ 
              display: 'block', 
              fontSize: '12px', 
              fontWeight: '500', 
              color: '#374151', 
              marginBottom: '4px' 
            }}>
              Clause Content
            </label>
            <textarea
              value={newClause.content}
              onChange={(e) => setNewClause({ ...newClause, content: e.target.value })}
              rows="4"
              style={{
                width: '100%',
                padding: '8px 10px',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                fontSize: '12px',
                resize: 'vertical',
                fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
              }}
              placeholder="Enter clause content..."
            />
          </div>

          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center' 
          }}>
            <label style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '6px', 
              fontSize: '12px', 
              color: '#374151',
              cursor: 'pointer'
            }}>
              <input
                type="checkbox"
                checked={newClause.isRequired}
                onChange={(e) => setNewClause({ ...newClause, isRequired: e.target.checked })}
                style={{ margin: 0 }}
              />
              Required Clause
            </label>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                type="button"
                onClick={handleCancel}
                style={{
                  padding: '6px 12px',
                  backgroundColor: '#f3f4f6',
                  color: '#374151',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  fontSize: '12px',
                  cursor: 'pointer',
                  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={editingClause ? handleUpdateClause : handleAddClause}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  padding: '6px 12px',
                  backgroundColor: '#007B8F',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  fontSize: '12px',
                  cursor: 'pointer',
                  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
                }}
              >
                <Save size={12} />
                {editingClause ? 'Update' : 'Add'} Clause
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Clauses List */}
      <div style={{ 
        border: '1px solid #e5e7eb', 
        borderRadius: '8px', 
        backgroundColor: 'white',
        maxHeight: '400px',
        overflowY: 'auto'
      }}>
        {clauses.length === 0 ? (
          <div style={{ 
            padding: '24px', 
            textAlign: 'center', 
            color: '#6b7280',
            fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
          }}>
            <FileText size={32} style={{ marginBottom: '8px', opacity: 0.5 }} />
            <div style={{ fontSize: '14px' }}>No clauses added yet</div>
            <div style={{ fontSize: '12px', marginTop: '4px' }}>
              Add clauses to define contract terms and conditions
            </div>
          </div>
        ) : (
          clauses.map((clause, index) => (
            <div
              key={clause.id}
              style={{
                padding: '12px 16px',
                borderBottom: index < clauses.length - 1 ? '1px solid #f3f4f6' : 'none',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                gap: '12px'
              }}
            >
              <div style={{ flex: 1 }}>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '8px', 
                  marginBottom: '4px' 
                }}>
                  <h6 style={{ 
                    margin: 0, 
                    fontSize: '13px', 
                    fontWeight: '600', 
                    color: '#374151',
                    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
                  }}>
                    {clause.title}
                  </h6>
                  <span style={{
                    padding: '2px 6px',
                    backgroundColor: getCategoryColor(clause.category),
                    color: 'white',
                    borderRadius: '4px',
                    fontSize: '10px',
                    fontWeight: '500'
                  }}>
                    {clause.category}
                  </span>
                  {clause.isRequired && (
                    <span style={{
                      padding: '2px 6px',
                      backgroundColor: '#ef4444',
                      color: 'white',
                      borderRadius: '4px',
                      fontSize: '10px',
                      fontWeight: '500'
                    }}>
                      Required
                    </span>
                  )}
                  {clause.isCustom && (
                    <span style={{
                      padding: '2px 6px',
                      backgroundColor: '#8b5cf6',
                      color: 'white',
                      borderRadius: '4px',
                      fontSize: '10px',
                      fontWeight: '500'
                    }}>
                      Custom
                    </span>
                  )}
                </div>
                <div style={{ 
                  fontSize: '12px', 
                  color: '#6b7280', 
                  lineHeight: '1.4',
                  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
                }}>
                  {clause.content}
                </div>
              </div>
              <div style={{ display: 'flex', gap: '4px' }}>
                <button
                  type="button"
                  onClick={() => handleEditClause(clause)}
                  style={{
                    padding: '4px',
                    backgroundColor: '#f3f4f6',
                    color: '#374151',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <Edit size={12} />
                </button>
                <button
                  type="button"
                  onClick={() => handleDeleteClause(clause.id)}
                  style={{
                    padding: '4px',
                    backgroundColor: '#fef2f2',
                    color: '#ef4444',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <Trash2 size={12} />
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Clause Selector Modal */}
      {showClauseSelector && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '90vw',
            maxHeight: '90vh',
            width: '800px',
            overflow: 'auto',
            position: 'relative'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px'
            }}>
              <h3 style={{
                margin: 0,
                fontSize: '18px',
                fontWeight: '600',
                color: '#1f2937'
              }}>
                Select Clauses from Library
              </h3>
              <button
                onClick={() => setShowClauseSelector(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '4px',
                  color: '#6b7280'
                }}
              >
                <X size={20} />
              </button>
            </div>
            
            <ClauseSelector
              onClauseSelect={(clause) => {
                // Add clause to the clauses list
                const newClause = {
                  ...clause,
                  id: clause._id || Date.now().toString(),
                  isCustom: false
                };
                onClausesChange([...clauses, newClause]);
                setShowClauseSelector(false);
              }}
              onClauseInsert={(clause) => {
                // Insert clause content directly
                const newClause = {
                  ...clause,
                  id: clause._id || Date.now().toString(),
                  isCustom: false
                };
                onClausesChange([...clauses, newClause]);
                setShowClauseSelector(false);
              }}
              contractType={contractType}
              contractContext={{}}
              selectedClauses={[]}
              onSelectedClausesChange={() => {}}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ClauseManager;
